import { useSyncExternalStore } from "react";
import { resetRelay } from "@/lib/store";

export interface AdminUser {
  username: string;
  loginAt: string;
}

interface AuthSnap {
  user: AdminUser | null;
  ready: boolean;
}

const EMPTY: AuthSnap = { user: null, ready: false };
let authSnap: AuthSnap = EMPTY;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((fn) => fn());
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot() {
  return authSnap;
}

function setAuth(user: AdminUser | null) {
  authSnap = { user, ready: true };
  emit();
}

async function refreshSession() {
  try {
    const response = await fetch("/api/session", { credentials: "same-origin" });
    if (!response.ok) {
      setAuth(null);
      return;
    }
    const data = (await response.json()) as AdminUser;
    setAuth({ username: data.username, loginAt: data.loginAt });
  } catch {
    setAuth(null);
  }
}

void refreshSession();

export async function adminLogin(username: string, password: string): Promise<{ ok: boolean; message?: string }> {
  try {
    const response = await fetch("/api/login", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    const data = (await response.json()) as { ok?: boolean; message?: string; username?: string; loginAt?: string };
    if (!response.ok || !data.ok || !data.username) {
      return { ok: false, message: data.message || "账号或密码不正确，请重试" };
    }
    setAuth({ username: data.username, loginAt: data.loginAt || new Date().toISOString() });
    return { ok: true };
  } catch {
    return { ok: false, message: "跳转服务没有连上" };
  }
}

export async function adminLogout() {
  try {
    await fetch("/api/logout", { method: "POST", credentials: "same-origin" });
  } catch {
    // still clear the local session
  }
  resetRelay();
  setAuth(null);
}

export function useAuth() {
  const snap = useSyncExternalStore(subscribe, getSnapshot, () => EMPTY);
  return { user: snap.user, isAuthed: !!snap.user, ready: snap.ready };
}
